/**
 * The landing page: every account this FDE looks after, and what needs doing.
 *
 * A table, not cards. Cards were the original call and it was wrong: the question
 * this page answers is "which account needs me, and is anything quietly broken?",
 * and the answer is a set of numbers you want to *compare down a column*. Three
 * cards side by side make you read each one separately and hold the numbers in your
 * head. This follows 8loop's own list-page anatomy — PageHeader, ActionBar, dense
 * table — so it reads as the same product.
 *
 * The "Dialling" column is the load-bearing one. Three separate states silently stop
 * an account calling anybody — paused, no calling hours, no working credentials — and
 * every one of them looks completely healthy in a list of names.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import type { ColumnDef } from "@tanstack/react-table";
import { Ban, Clock, PhoneOutgoing, Plus, Settings, ShieldBan } from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { NewAccountDialog } from "@/components/new-account-dialog";
import { TableSkeleton } from "@/components/table-skeleton";
import { PageHeader } from "@/components/ui/page-header";
import { ActionBar, ActionBarSearch } from "@/components/ui/action-bar";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import { DenseTableViewport } from "@/components/ui/dense-table-viewport";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Badge } from "@/components/ui/badge";
import { RowActionsMenu } from "@/components/ui/row-actions-menu";
import { errorMessage } from "@/lib/api";
import { relativeTime } from "@/lib/labels";
import { useAccounts, useMe } from "@/lib/queries";
import { TABLE_ROW_GROUP_CLASS } from "@/lib/table-styles";
import type { AccountWithQueue } from "@/lib/api";

export function AccountsPage() {
  const { data, isPending, isError, error, refetch } = useAccounts();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    document.title = "Accounts · Callbacks";
  }, []);

  const accounts = data?.accounts ?? [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return accounts;
    return accounts.filter(
      (a) =>
        a.name.toLowerCase().includes(needle) ||
        a.tenant_host.toLowerCase().includes(needle) ||
        a.slug.toLowerCase().includes(needle),
    );
  }, [accounts, search]);

  const columns = useAccountColumns();
  const isAdmin = me?.user?.is_admin ?? false;

  return (
    <AppShell active="accounts">
      <div className="flex flex-col h-full w-full max-w-full">
        <PageHeader
          title="Accounts"
          description="Every 8loop tenant this app can call on behalf of. Open one to see who is waiting for a call back."
          actionsSlot={
            <>
              {me?.dry_run && (
                <Badge variant="outline" className="hidden gap-1.5 text-[11px] sm:inline-flex">
                  <Ban className="size-3" />
                  Dry run — no calls are placed
                </Badge>
              )}
              {/*
                Admin-only server-side (`requireAdmin` on POST /accounts), so the
                button is hidden rather than shown-and-403'd. Non-admins get the
                reason in the empty state instead of a dead control.
              */}
              {isAdmin && (
                <Button size="sm" onClick={() => setCreating(true)}>
                  <Plus className="size-3.5" />
                  New account
                </Button>
              )}
            </>
          }
        />

        <div className="flex min-h-0 flex-1 flex-col gap-4 px-6">
          {accounts.length > 0 && (
            <ActionBar
              left={
                <span className="px-1 text-xs text-muted-foreground tabular-nums">
                  {filtered.length === accounts.length
                    ? `${accounts.length} account${accounts.length === 1 ? "" : "s"}`
                    : `${filtered.length} of ${accounts.length}`}
                </span>
              }
              right={
                <ActionBarSearch
                  value={search}
                  onValueChange={setSearch}
                  onClear={() => setSearch("")}
                  placeholder="Account or tenant"
                  /* The placeholder is the only label this box has, and it vanishes
                     the moment anyone types — so the name has to come from here. */
                  aria-label="Search accounts by name or tenant host"
                />
              }
            />
          )}

          <div className="min-h-0 flex-1 pb-6">
            {isError ? (
              <ErrorState
                title="Couldn't load your accounts"
                description={errorMessage(error)}
                onRetry={() => refetch()}
              />
            ) : isPending ? (
              <TableSkeleton columnCount={6} rowCount={4} />
            ) : accounts.length === 0 ? (
              <EmptyState
                title="No accounts yet"
                description={
                  isAdmin
                    ? "Add the first tenant. You will need its 8loop host (e.g. equentis.8loop.ai) and an API key with the INITIATE_CALLS scope."
                    : "An admin adds accounts and grants access to them. Once one is yours it appears here with its callback queue."
                }
                action={
                  isAdmin ? (
                    <Button size="sm" onClick={() => setCreating(true)}>
                      <Plus className="size-3.5" />
                      New account
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              <DenseTableViewport>
                <DataTable
                  columns={columns}
                  data={filtered}
                  onRowClick={(row) => navigate(`/accounts/${row.id}/queue`)}
                  /*
                    `cursor-pointer` is the fix for the bug Arun reported: "when i hover
                    account name, i see the curosor used for text input - not the click
                    pointer tht tells me i can click". The whole row navigates, but
                    nothing in the class chain set a cursor — TableRow only brings
                    `hover:bg-row-hover` — so the <tr> computed `cursor: auto` and over
                    text that renders as an I-beam. The row highlighting on hover while
                    the cursor said "select this text" is what made it feel broken.
                  */
                  rowClassName={`${TABLE_ROW_GROUP_CLASS} cursor-pointer`}
                  rowHeightClassName="h-12"
                  headerHeightClassName="h-8"
                  stickyHeader
                  emptyContent={
                    <EmptyState
                      size="compact"
                      title="No account matches that"
                      description="Clear the search to see all of them again."
                    />
                  }
                />
              </DenseTableViewport>
            )}
          </div>
        </div>
      </div>

      <NewAccountDialog open={creating} onOpenChange={setCreating} />
    </AppShell>
  );
}

/**
 * Columns.
 *
 * "Dialling" comes second, right after the name, because an account that cannot
 * dial makes every number to its right meaningless — a queue of 40 due callbacks on
 * a paused account is not 40 calls about to happen, it is 40 calls that will never
 * happen. Status is coloured text, not a badge, per the design system: badges here
 * are reserved for identifiers.
 */
function useAccountColumns(): ColumnDef<AccountWithQueue>[] {
  return useMemo(
    () => [
      {
        id: "account",
        header: "Account",
        size: 230,
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col justify-center leading-tight">
            {/*
              A real <Link>, not a <span>. The row click stays as the convenience it
              was meant to be, but the name now carries the semantics people expect
              of it: a pointer cursor, a hover underline, a focus ring, keyboard
              reachability, and a middle-click that opens the account in a new tab.
              `stopPropagation` keeps the row handler from navigating a second time.
            */}
            <Link
              to={`/accounts/${row.original.id}/queue`}
              onClick={(e) => e.stopPropagation()}
              className="truncate rounded-sm font-medium hover:underline focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {row.original.name}
            </Link>
            <span className="truncate font-mono text-[11px] text-muted-foreground">
              {row.original.tenant_host}
            </span>
          </div>
        ),
      },
      {
        id: "dialling",
        header: "Dialling",
        size: 210,
        cell: ({ row }) => <DiallingCell account={row.original} />,
      },
      {
        // `dueNow` is the subset whose time has already passed — the number the rail
        // badge shows. Called "Ready now" rather than "Due now" because the queue
        // page's own pill says "Due" for the whole scheduled group, and having the
        // same word mean two different counts on two adjacent screens is how you
        // get someone reading the wrong number.
        id: "ready",
        header: "Ready now",
        size: 100,
        cell: ({ row }) => <Count value={row.original.queue.dueNow} tone="text-warning" />,
      },
      {
        id: "scheduled",
        header: "Scheduled",
        size: 100,
        cell: ({ row }) => (
          <Count value={row.original.queue.byGroup?.due ?? 0} tone="text-foreground" />
        ),
      },
      {
        id: "attention",
        header: "Needs attention",
        size: 140,
        cell: ({ row }) => (
          <Count value={row.original.queue.byGroup?.attention ?? 0} tone="text-destructive" />
        ),
      },
      {
        id: "next",
        header: "Next call",
        size: 130,
        cell: ({ row }) => {
          const next = row.original.queue.nextDueAt;
          if (!next) return <span className="text-muted-foreground">—</span>;
          /*
            Clock time first, "in 20 minutes" underneath — the queue's When column
            already works this way and this cell was the odd one out. A relative time
            on its own cannot be acted on: "in 4 hours" does not tell you whether that
            lands inside this tenant's calling hours, and it silently goes stale while
            the page sits open. The absolute time is in the account's own zone, which
            is why the Timezone column is right next to it.
          */
          return (
            <div className="flex min-w-0 flex-col justify-center leading-tight">
              <span className="inline-flex items-center gap-1.5 text-xs">
                <Clock className="size-3 text-muted-foreground" />
                {DateTime.fromISO(next).setZone(row.original.timezone).toFormat("d LLL, HH:mm")}
              </span>
              <span className="pl-[18px] text-[11px] text-muted-foreground">
                {relativeTime(next)}
              </span>
            </div>
          );
        },
      },
      {
        id: "timezone",
        header: "Timezone",
        size: 120,
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">{row.original.timezone}</span>
        ),
      },
      {
        // Last column, so DataTable pins it against the right edge. The sizes above
        // are deliberately kept under the available width — overflow pushed this
        // one past the canvas card and the menu became invisible.
        id: "actions",
        header: "",
        size: 44,
        cell: ({ row }) => <RowActions id={row.original.id} />,
      },
    ],
    [],
  );
}

/** The row menu. Split out so it can use the router hook a column cell cannot. */
function RowActions({ id }: { id: string }) {
  const navigate = useNavigate();
  return (
    <RowActionsMenu
      triggerLabel="Open this account"
      actions={[
        { label: "Callback queue", icon: PhoneOutgoing, onSelect: () => navigate(`/accounts/${id}/queue`) },
        { label: "Do not call", icon: ShieldBan, onSelect: () => navigate(`/accounts/${id}/suppressions`) },
        { label: "Configuration", icon: Settings, onSelect: () => navigate(`/accounts/${id}/settings`) },
      ]}
    />
  );
}

/**
 * Why this account is or is not going to ring anybody.
 *
 * Ordered by which one wins: paused beats everything, then no credentials (cannot
 * authenticate), then no calling hours (nothing is ever open), then a credential
 * that will not decrypt. Only the first is shown — a stack of three red lines in a
 * table cell is noise, and fixing the first often reveals the next anyway.
 */
function DiallingCell({ account }: { account: AccountWithQueue }) {
  const problem = accountProblem(account);
  if (!problem) {
    return <span className="text-xs text-success">Active</span>;
  }
  return (
    <div className="flex min-w-0 flex-col justify-center leading-tight">
      <span className={`text-xs ${problem.tone}`}>{problem.label}</span>
      <span className="truncate text-[11px] text-muted-foreground" title={problem.detail}>
        {problem.detail}
      </span>
    </div>
  );
}

export function accountProblem(
  account: AccountWithQueue,
): { label: string; detail: string; tone: string } | null {
  if (account.paused) {
    return {
      label: "Paused",
      detail: "nothing is dialled until it is resumed",
      tone: "text-warning",
    };
  }
  if (account.credentials.mode === "none") {
    return {
      label: "No 8loop credentials",
      detail: "add an API key on Setup",
      tone: "text-destructive",
    };
  }
  if (account.credentials.credential_error) {
    return {
      label: "Credential unreadable",
      detail: account.credentials.credential_error,
      tone: "text-destructive",
    };
  }
  if (account.calling_window_count === 0) {
    return {
      label: "No calling hours",
      detail: "callbacks queue up and are never dialled",
      tone: "text-destructive",
    };
  }
  return null;
}

/** A count that goes quiet at zero, so a column of noughts does not shout. */
function Count({ value, tone }: { value: number; tone: string }) {
  return (
    <span className={`tabular-nums ${value > 0 ? tone : "text-muted-foreground"}`}>
      {value > 0 ? value : "—"}
    </span>
  );
}
